/**
 * Typovaný REST klient gateway (S11, F4AI-17).
 *
 * Dizajn:
 *  - `createApi({ baseUrl, fetch, timeoutMs })` — VŠETKO je injectable,
 *    takže testy nepotrebujú MSW ani live gateway: stačí injectnúť
 *    `fetch: mockFetch` (každý test si postaví vlastnú mock funkciu).
 *  - `ApiError` nesie HTTP status + preparsedé teliesko gateway
 *    (`GatewayErrorBody`), aby stránky vedeli rozlíšiť 404 / 503 / 400.
 *  - each request beží cez `AbortController` s `timeoutMs` (default 10 s),
 *    lebo gateway môže visieť na relayi (pool.query timeout 5 s + retry).
 *
 * Base URL: `VITE_GATEWAY_URL` (Vite env), default
 * `http://192.168.77.177:18081` — dev gateway na `.177`.
 */
import type {
  FeedResponse,
  GatewayErrorBody,
  HealthResponse,
  IdeaDetailResponse,
  IdeasResponse,
  PublishResponse,
  RecentResponse,
  ReputationDomain,
  ReputationResponse,
  SearchResponse,
  ThreadResponse,
  TopicDetailResponse,
  TopicsResponse,
  PublishableEvent,
} from './types.js';

export const DEFAULT_GATEWAY_URL = 'http://192.168.77.177:18081';

/** podpis funkcie, ktorú klient skutočne volá — `fetch`-kompatibilný */
export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiOptions {
  /** default `VITE_GATEWAY_URL` alebo `DEFAULT_GATEWAY_URL` */
  baseUrl?: string;
  /** default `globalThis.fetch` */
  fetch?: FetchLike;
  /** default 10 000 ms; 0 = bez timeoutu */
  timeoutMs?: number;
}

export interface PagedParams {
  limit?: number;
}

export interface FeedParams extends PagedParams {
  /** 1020/1021/1022 pre /feed; ľubovoľný kind z registry pre /recent */
  kinds?: number[];
  authors?: string[];
}

export interface SearchParams extends PagedParams {
  q: string;
  kinds?: number[];
  authors?: string[];
  since?: number;
  until?: number;
}

export interface IdeasParams extends PagedParams {
  status?: string;
  topic?: string;
  since?: number;
  /** B2: presný pubkey autora — server-side filter (`?author=<64hex>`) */
  author?: string;
}

/**
 * Chyba REST vrstvy. `status === 0` znamená, že request nedopadol
 * (DNS/offline/abort) — teliesko `body` vtedy nie je JSON.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly url: string;

  constructor(message: string, status: number, body: unknown, url: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.url = url;
  }

  /** `error` + `reasons`/`reason` z gateway telieska — na zobrazenie v UI */
  get reasons(): string[] {
    if (this.body !== null && typeof this.body === 'object') {
      const body = this.body as GatewayErrorBody;
      if (Array.isArray(body.reasons)) return body.reasons.map(String);
      if (typeof body.reason === 'string') return [body.reason];
      if (typeof body.error === 'string') return [body.error];
    }
    return [this.message];
  }

  get code(): string {
    if (this.body !== null && typeof this.body === 'object') {
      const error = (this.body as GatewayErrorBody).error;
      if (typeof error === 'string') return error;
    }
    return `http_${String(this.status)}`;
  }
}

/**
 * Prečíta `VITE_GATEWAY_URL` z Vite env. `env` je parameter (default
 * `import.meta.env`) len pre testy — v aplikácii sa volá bez argumentu.
 */
export function gatewayBaseUrl(env: Record<string, string | undefined> = import.meta.env): string {
  const raw = env['VITE_GATEWAY_URL'];
  if (typeof raw === 'string' && raw.trim() !== '') return trimSlash(raw.trim());
  return DEFAULT_GATEWAY_URL;
}

function trimSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

/** query reťazec z objektu — polia spája čiarkou (formát gateway parserov) */
export function buildQuery(params: Record<string, unknown> | undefined): string {
  if (params === undefined) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      search.set(key, value.join(','));
      continue;
    }
    search.set(key, String(value));
  }
  const query = search.toString();
  return query === '' ? '' : `?${query}`;
}

export interface Api {
  readonly baseUrl: string;
  health(): Promise<HealthResponse>;
  topics(): Promise<TopicsResponse>;
  topic(slug: string, params?: PagedParams): Promise<TopicDetailResponse>;
  thread(id: string): Promise<ThreadResponse>;
  feed(params?: FeedParams): Promise<FeedResponse>;
  recent(params?: FeedParams): Promise<RecentResponse>;
  search(params: SearchParams): Promise<SearchResponse>;
  reputation(pubkey: string, domain?: ReputationDomain): Promise<ReputationResponse>;
  ideas(params?: IdeasParams): Promise<IdeasResponse>;
  idea(id: string): Promise<IdeaDetailResponse>;
  publish(event: PublishableEvent): Promise<PublishResponse>;
}

export function createApi(options: ApiOptions = {}): Api {
  const baseUrl = trimSlash(options.baseUrl ?? gatewayBaseUrl());
  const doFetch: FetchLike = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const timeoutMs = options.timeoutMs ?? 10_000;

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const url = `${baseUrl}${path}`;
    const controller = timeoutMs > 0 ? new AbortController() : null;
    const timer =
      controller !== null
        ? setTimeout(() => {
            controller.abort();
          }, timeoutMs)
        : null;

    let response: Response;
    try {
      response = await doFetch(url, {
        ...init,
        ...(controller !== null ? { signal: controller.signal } : {}),
      });
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      // reason ide do error panelu — pridáme URL a hint na env, aby
      // používáč vedel odlíšiť „gateway beží inde“ od „gateway neběží“
      throw new ApiError(
        `gateway nedostupný (${reason})`,
        0,
        {
          ok: false,
          error: 'network error',
          reason: `${reason} — gateway na ${url} neodpovedá (skús VITE_GATEWAY_URL=http://127.0.0.1:3001)`,
        },
        url,
      );
    } finally {
      if (timer !== null) clearTimeout(timer);
    }

    const text = await response.text();
    let parsed: unknown = null;
    if (text !== '') {
      try {
        parsed = JSON.parse(text) as unknown;
      } catch {
        parsed = text;
      }
    }

    if (!response.ok) {
      const body = parsed ?? { ok: false, error: `http_${String(response.status)}` };
      const code =
        body !== null && typeof body === 'object' && 'error' in body
          ? String((body as { error: unknown }).error)
          : `http_${String(response.status)}`;
      throw new ApiError(`${code} (${String(response.status)}) na ${path}`, response.status, body, url);
    }

    if (parsed === null || typeof parsed !== 'object') {
      throw new ApiError(`očakávali sme JSON odpoveď na ${path}`, response.status, parsed, url);
    }
    return parsed as T;
  }

  return {
    baseUrl,
    health: () => request<HealthResponse>('/health'),
    topics: () => request<TopicsResponse>('/topics'),
    topic: (slug, params) =>
      request<TopicDetailResponse>(`/topics/${encodeURIComponent(slug)}${buildQuery(params as Record<string, unknown> | undefined)}`),
    thread: (id) => request<ThreadResponse>(`/threads/${encodeURIComponent(id)}`),
    feed: (params) => request<FeedResponse>(`/feed${buildQuery(params as Record<string, unknown> | undefined)}`),
    recent: (params) => request<RecentResponse>(`/recent${buildQuery(params as Record<string, unknown> | undefined)}`),
    search: (params) => request<SearchResponse>(`/search${buildQuery(params as unknown as Record<string, unknown>)}`),
    reputation: (pubkey, domain) =>
      request<ReputationResponse>(
        `/reputation/${encodeURIComponent(pubkey)}${buildQuery(domain !== undefined ? { domain } : undefined)}`,
      ),
    ideas: (params) => request<IdeasResponse>(`/ideas${buildQuery(params as Record<string, unknown> | undefined)}`),
    idea: (id) => request<IdeaDetailResponse>(`/ideas/${encodeURIComponent(id)}`),
    publish: (event) =>
      request<PublishResponse>('/events', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(event),
      }),
  };
}

/**
 * Default klient pre produkčné volania (používajú ho stránky bez vlastného
 * injectnutia). Testy a `ApiProvider` vytvárajú vlastné inštancie.
 */
export const api: Api = createApi();
